/**
 * UNIFIED INBOX MODEL — the seam that lets every "waiting on you" surface show
 * email, tasks, and (soon) texts as ONE list of rows instead of channel silos.
 *
 * A channel is just an icon on a row. Adding texting later means writing
 * `textToRow()` and dropping its rows into the same list — NOT a fourth badge to
 * ignore. Adapters are pure (no auth/masking) so they're trivial to test and the
 * caller stays in control of money-masking.
 */
import type { InboxItem } from "./tasks";
import type { MailType } from "./mail-count";

export type InboxChannel = "email" | "task" | "text";

// ── Mailroom peek payload (GET /api/mailbox/inbox-peek) ──────────────────────
// Canonical home for the toolbar's email-peek types so the door and any other
// surface share one shape. `count` is the whole team's open backlog (the old
// badge); `newCount` is threads THIS user has never opened — the clearable one.
export interface MailPeekItem {
    id: string;
    from: string;
    subject: string | null;
    snippet: string | null;
    lastMessageAt: string | null;
    fromCustomer: boolean;
    weRepliedLast: boolean;
    /** Which mailbox this thread lives in — carried so the deep-link opens the
     *  reader on the right box (the Mailroom scopes its lists per account). */
    accountId: string | null;
    /** This viewer personally hasn't opened it — the "new to me" hint. */
    newToViewer: boolean;
    // ── 10/9 friendly Mailroom: what the preview needs to draw a tile + one button.
    workState?: "needs_work" | "working";
    mailType?: MailType;
    assigneeId?: string | null;
    assignee?: string | null;
    attachmentCount?: number;
    /** Working hours since the newest message, and its short label ("2h", "3d"). */
    waitedHours?: number;
    waitedLabel?: string;
    customerName?: string | null;
    jobLabel?: string | null;
}
/**
 * Since 10/9 `count` uses the honest rules — it is the same number as
 * GET /mailbox/count's `needYou` (emails only; tasks have their own counter).
 */
export interface MailPeek {
    count: number;
    newCount: number;
    threads: MailPeekItem[];
    onTeam?: boolean;
    oldestWaitingLabel?: string | null;
    team?: { open: number; waiting: number };
}

// ── The one row shape every surface renders ──────────────────────────────────
export interface UnifiedRow {
    /** Stable React key — channel-prefixed because ids can collide across channels. */
    key: string;
    channel: InboxChannel;
    title: string;
    /** Secondary line: subject preview for email, due/priority for a task. */
    sub: string;
    /** ISO timestamp used for sort + a relative label; null when unknown. */
    when: string | null;
    /** Where a click goes — always a real destination, never a generic fallback. */
    href: string;
    /** Loud priority (urgent task / known customer). */
    urgent: boolean;
    /** Aged past its window / overdue — the scarce amber signal. */
    breaching: boolean;
    /** Personally unopened / on me — drives the "On you" filter + clearable count. */
    newToMe: boolean;
    /** Seed for the email avatar tint (email rows only). */
    avatarSeed?: string;
}

/** Deep-link to a Mailroom thread, carrying its box so the reader switches to it. */
export function mailThreadHref(id: string, accountId: string | null, step?: string): string {
    const base = accountId ? `/mailroom?thread=${id}&account=${accountId}` : `/mailroom?thread=${id}`;
    return step ? `${base}&step=${step}` : base;
}

/** A due date is breaching once its day has passed (date-only, local midnight). */
function dueIsPast(dueDate: string | null): boolean {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    if (Number.isNaN(due.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due.getTime() < today.getTime();
}

export function mailToRow(m: MailPeekItem): UnifiedRow {
    return {
        key: `email:${m.id}`,
        channel: "email",
        title: m.from,
        sub: m.subject || "(no subject)",
        when: m.lastMessageAt,
        href: mailThreadHref(m.id, m.accountId),
        urgent: m.fromCustomer,
        breaching: false, // per-thread breach isn't in the peek; the door's amber uses the dropped count
        newToMe: m.newToViewer,
        avatarSeed: m.from,
    };
}

export function taskToRow(t: InboxItem): UnifiedRow {
    return {
        key: `task:${t.id}`,
        channel: "task",
        title: t.title,
        sub: t.dueDate ? `due ${t.dueDate}` : t.priority === "urgent" ? "Urgent" : "On you",
        when: t.dueDate ?? t.createdAt,
        href: t.href || "/tasks",
        urgent: t.priority === "urgent",
        breaching: dueIsPast(t.dueDate),
        // A task in your inbox is by definition on you — always shows under "On you".
        newToMe: true,
    };
}

/**
 * Sort a unified list the way a person triages: the amber stuff first (breaching,
 * then urgent), then most-recent. Stable and pure.
 */
export function sortUnified(rows: UnifiedRow[]): UnifiedRow[] {
    const rank = (r: UnifiedRow) => (r.breaching ? 2 : 0) + (r.urgent ? 1 : 0);
    return [...rows].sort((a, b) => {
        const byRank = rank(b) - rank(a);
        if (byRank !== 0) return byRank;
        const ta = a.when ? new Date(a.when).getTime() : 0;
        const tb = b.when ? new Date(b.when).getTime() : 0;
        return tb - ta;
    });
}
